import React, { useEffect, useRef, useCallback } from 'react';

/* ─────────────────────────────────────────────────────────────
   StarBackground — full-page canvas animation
   • Twinkling stars  (white + gold, visible gold touch)
   • Gold nebula glow patches (ambient background glow)
   • Shooting stars   (diagonal streaks — mostly gold)
   • Mouse hover      (nearby stars repel gently)
   • Subtle gold radial gradient overlay
   ───────────────────────────────────────────────────────────── */

const STAR_COUNT = 260;
const SHOOT_FREQ = 2400;  // ms between shooting stars

/* 
  Color distribution:
  white stars = ~50%, small gold = ~30%, bright gold = ~20%
*/
const COLORS = [
  '#ffffff', '#ffffff', '#ffffff',          // white
  '#F2B134', '#F2B134',                     // gold
  '#ffcc6b',                                // light gold
  '#fffde7',                                // warm white
];

const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr)       => arr[Math.floor(Math.random() * arr.length)];

function createStar(W, H) {
  const isGold = Math.random() > 0.55;
  return {
    x:       rand(0, W),
    y:       rand(0, H),
    r:       isGold ? rand(0.5, 2.2) : rand(0.25, 1.5),
    color:   isGold ? pick(['#F2B134', '#ffcc6b', '#ffd966']) : pick(['#ffffff', '#fffde7']),
    alpha:   isGold ? rand(0.08, 0.32) : rand(0.04, 0.18),
    twinkleSpeed: rand(0.003, 0.014),
    twinkleDir:   Math.random() > 0.5 ? 1 : -1,
    twinkleMin:   isGold ? 0.05 : 0.02,
    twinkleMax:   isGold ? 0.38 : 0.22,
    vx: rand(-0.035, 0.035),
    vy: rand(-0.035, 0.035),
    isGold,
    ox: 0, oy: 0,
  };
}

function createShooter(W) {
  const isGold  = Math.random() > 0.35;   // 65% gold shooters
  const startX  = rand(W * 0.05, W * 0.85);
  const startY  = rand(0, 180);
  const angle   = rand(22, 52);
  const rad     = (angle * Math.PI) / 180;
  const speed   = rand(7, 16);
  return {
    x: startX, y: startY,
    dx: Math.cos(rad) * speed,
    dy: Math.sin(rad) * speed,
    len: rand(90, 240),
    alpha: rand(0.6, 0.95),
    life: 1,
    decay: rand(0.010, 0.022),
    trail: [],
    color: isGold ? (Math.random() > 0.5 ? '#F2B134' : '#ffcc6b') : '#ffffff',
    isGold,
  };
}

/* Nebula glow patches (static per session, rebuilt on resize) */
function createNebulas(W, H) {
  return [
    // gold nebulas
    { x: W * 0.15, y: H * 0.2,  r: W * 0.28, alpha: 0.045, color: '#F2B134' },
    { x: W * 0.82, y: H * 0.55, r: W * 0.25, alpha: 0.035, color: '#F2B134' },
    { x: W * 0.5,  y: H * 0.85, r: W * 0.20, alpha: 0.030, color: '#ffcc6b' },
    // white/cool nebulas
    { x: W * 0.65, y: H * 0.12, r: W * 0.18, alpha: 0.025, color: '#ffffff' },
    { x: W * 0.3,  y: H * 0.7,  r: W * 0.22, alpha: 0.020, color: '#ffffff' },
  ];
}

const StarBackground = () => {
  const canvasRef     = useRef(null);
  const starsRef      = useRef([]);
  const nebulasRef    = useRef([]);
  const shootersRef   = useRef([]);
  const mouseRef      = useRef({ x: -9999, y: -9999 });
  const rafRef        = useRef(null);
  const shootTimerRef = useRef(null);

  const launch = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    shootersRef.current.push(createShooter(canvas.width));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    let W, H;

    const resize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
      starsRef.current   = Array.from({ length: STAR_COUNT }, () => createStar(W, H));
      nebulasRef.current = createNebulas(W, H);
    };

    resize();
    window.addEventListener('resize', resize);

    const onMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMove);

    /* shooting star scheduler */
    const scheduleShoots = () => {
      launch();
      // sometimes fire 2 at once (rare double-streak)
      if (Math.random() > 0.82) setTimeout(launch, rand(120, 400));
      shootTimerRef.current = setTimeout(scheduleShoots, rand(SHOOT_FREQ * 0.5, SHOOT_FREQ * 1.4));
    };
    shootTimerRef.current = setTimeout(scheduleShoots, 600);

    /* ── DRAW LOOP ─────────────────────────────────────────── */
    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      /* ── 1. Nebula glow patches (drawn first, behind everything) ── */
      for (const n of nebulasRef.current) {
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r);
        grad.addColorStop(0,   hexAlpha(n.color, n.alpha));
        grad.addColorStop(0.5, hexAlpha(n.color, n.alpha * 0.3));
        grad.addColorStop(1,   hexAlpha(n.color, 0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      /* ── 2. Stars ── */
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const REPEL_R = 130;
      const REPEL_F = 20;

      for (const s of starsRef.current) {
        // drift
        s.x += s.vx;  s.y += s.vy;
        if (s.x < 0) s.x = W;
        if (s.x > W) s.x = 0;
        if (s.y < 0) s.y = H;
        if (s.y > H) s.y = 0;

        // twinkle
        s.alpha += s.twinkleSpeed * s.twinkleDir;
        if (s.alpha > s.twinkleMax || s.alpha < s.twinkleMin) s.twinkleDir *= -1;
        s.alpha = Math.max(s.twinkleMin, Math.min(s.twinkleMax, s.alpha));

        // hover repulsion
        const dx   = s.x - mx;
        const dy   = s.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < REPEL_R && dist > 0) {
          const force = (1 - dist / REPEL_R) * REPEL_F;
          s.ox += (dx / dist) * force * 0.09;
          s.oy += (dy / dist) * force * 0.09;
        }
        s.ox *= 0.86;
        s.oy *= 0.86;

        const px = s.x + s.ox;
        const py = s.y + s.oy;

        ctx.fillStyle   = s.color;
        ctx.globalAlpha = s.alpha;
        ctx.beginPath();
        ctx.arc(px, py, s.r, 0, Math.PI * 2);
        ctx.fill();

        // gold stars get a stronger glow (simulated using larger layered circle)
        if (s.isGold) {
          ctx.fillStyle   = '#F2B134';
          ctx.globalAlpha = s.alpha * 0.25;
          ctx.beginPath();
          ctx.arc(px, py, s.r * 2.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      /* ── 3. Shooting stars ── */
      shootersRef.current = shootersRef.current.filter(sh => sh.life > 0);

      for (const sh of shootersRef.current) {
        sh.x    += sh.dx;
        sh.y    += sh.dy;
        sh.life -= sh.decay;

        sh.trail.push({ x: sh.x, y: sh.y });
        if (sh.trail.length > 26) sh.trail.shift();

        const opacity = sh.alpha * sh.life;
        if (opacity <= 0.01) continue;

        /* head glow */
        ctx.globalAlpha = opacity;
        ctx.fillStyle   = sh.color;
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, sh.isGold ? 2.2 : 1.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = opacity * 0.3;
        ctx.fillStyle   = sh.color;
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, sh.isGold ? 6 : 4, 0, Math.PI * 2);
        ctx.fill();

        /* gradient trail */
        if (sh.trail.length > 1) {
          const tail = sh.trail[0];
          const grad = ctx.createLinearGradient(sh.x, sh.y, tail.x, tail.y);
          grad.addColorStop(0,   hexAlpha(sh.color, opacity));
          grad.addColorStop(0.4, hexAlpha(sh.color, opacity * 0.4));
          grad.addColorStop(1,   hexAlpha(sh.color, 0));

          ctx.strokeStyle = grad;
          ctx.lineWidth   = sh.isGold ? 1.8 : 1.2;
          ctx.beginPath();
          ctx.moveTo(sh.x, sh.y);
          for (let i = sh.trail.length - 1; i >= 0; i--) {
            ctx.lineTo(sh.trail[i].x, sh.trail[i].y);
          }
          ctx.stroke();

          // Simulating trail shadow glow with double stroke
          ctx.strokeStyle = hexAlpha(sh.color, opacity * 0.12);
          ctx.lineWidth   = sh.isGold ? 4 : 2.5;
          ctx.beginPath();
          ctx.moveTo(sh.x, sh.y);
          for (let i = sh.trail.length - 1; i >= 0; i--) {
            ctx.lineTo(sh.trail[i].x, sh.trail[i].y);
          }
          ctx.stroke();
        }

        /* gold sparkle burst at head (small cross) */
        if (sh.isGold && sh.life > 0.3) {
          const sz = 3.5 * sh.life;
          ctx.globalAlpha = opacity * 0.6;
          ctx.strokeStyle = '#F2B135';
          ctx.lineWidth   = 0.8;
          ctx.beginPath();
          ctx.moveTo(sh.x - sz, sh.y); ctx.lineTo(sh.x + sz, sh.y);
          ctx.moveTo(sh.x, sh.y - sz); ctx.lineTo(sh.x, sh.y + sz);
          ctx.stroke();
        }
      }

      /* ── 4. Subtle full-canvas gold radial vignette (top-left warm glow) ── */
      const vigGrad = ctx.createRadialGradient(W * 0.12, H * 0.08, 0, W * 0.12, H * 0.08, W * 0.45);
      vigGrad.addColorStop(0,   'rgba(242,177,52,0.04)');
      vigGrad.addColorStop(0.6, 'rgba(242,177,52,0.015)');
      vigGrad.addColorStop(1,   'rgba(0,0,0,0)');
      ctx.fillStyle = vigGrad;
      ctx.fillRect(0, 0, W, H);

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(shootTimerRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
    };
  }, [launch]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      'fixed',
        top:           0,
        left:          0,
        width:         '100%',
        height:        '100%',
        zIndex:        0,
        pointerEvents: 'none',
        background:    '#000000',
      }}
      aria-hidden="true"
    />
  );
};

function hexAlpha(hex, a) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export default StarBackground;
